<template>
  <div class="change-password">
    <div class="change-password__container">
      <header class="change-password__header">
        <h3 class="change-password__title">Change password</h3>
        <p class="change-password__subtitle">Secure your account</p>
      </header>
      <div class="change-password__form-wrapper">
        <form @submit.prevent="submitData" class="change-password__form">
          <OnboardingPasswordInput
            label="Enter new password"
            :password="payload.password"
            :error="newPasswordError || {}"
            name="newPassword"
            :disabled="loading"
            @set="setNewPassword"
            border="change-password__input-border"
            @validate="validateMethod"
          />
          <div class="change-password__requirements">
            <div class="change-password__requirement">
              <svg v-if="passwordLength" class="change-password__check change-password__check--valid" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 0C3.582 0 0 3.582 0 8C0 12.418 3.582 16 8 16C12.418 16 16 12.418 16 8C16 3.582 12.418 0 8 0ZM6.667 11.333L3.333 8L4.547 6.787L6.667 8.893L11.453 4.107L12.667 5.333L6.667 11.333Z" fill="currentColor"/>
              </svg>
              <svg v-else class="change-password__check" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="7.5" stroke="currentColor"/>
              </svg>
              <span>At least 8 characters</span>
            </div>
            <div class="change-password__requirement">
              <svg v-if="containsNumber" class="change-password__check change-password__check--valid" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 0C3.582 0 0 3.582 0 8C0 12.418 3.582 16 8 16C12.418 16 16 12.418 16 8C16 3.582 12.418 0 8 0ZM6.667 11.333L3.333 8L4.547 6.787L6.667 8.893L11.453 4.107L12.667 5.333L6.667 11.333Z" fill="currentColor"/>
              </svg>
              <svg v-else class="change-password__check" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="7.5" stroke="currentColor"/>
              </svg>
              <span>At least one number</span>
            </div>
            <div class="change-password__requirement">
              <svg v-if="containsUppercase" class="change-password__check change-password__check--valid" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 0C3.582 0 0 3.582 0 8C0 12.418 3.582 16 8 16C12.418 16 16 12.418 16 8C16 3.582 12.418 0 8 0ZM6.667 11.333L3.333 8L4.547 6.787L6.667 8.893L11.453 4.107L12.667 5.333L6.667 11.333Z" fill="currentColor"/>
              </svg>
              <svg v-else class="change-password__check" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="7.5" stroke="currentColor"/>
              </svg>
              <span>At least one uppercase letter</span>
            </div>
            <div class="change-password__requirement">
              <svg v-if="containsSpecial" class="change-password__check change-password__check--valid" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 0C3.582 0 0 3.582 0 8C0 12.418 3.582 16 8 16C12.418 16 16 12.418 16 8C16 3.582 12.418 0 8 0ZM6.667 11.333L3.333 8L4.547 6.787L6.667 8.893L11.453 4.107L12.667 5.333L6.667 11.333Z" fill="currentColor"/>
              </svg>
              <svg v-else class="change-password__check" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="7.5" stroke="currentColor"/>
              </svg>
              <span>At least one special character</span>
            </div>
          </div>
          <OnboardingPasswordInput
            class="change-password__confirm-field"
            label="Confirm new password"
            :password="payload.confirmPassword"
            :error="confirmPasswordError || {}"
            name="confirmPassword"
            border="change-password__input-border"
            :disabled="enableConfirmPassword"
            @set="setConfirmPassword"
            @validate="validateMethod"
          />
          <button class="change-password__submit" :disabled="loading">
            <span v-if="!loading">Change Password</span>
            <img
              v-if="loading"
              class="change-password__spinner"
              src="@/assets/img/spinner.svg"
              alt="loading"
            />
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import useValidate from "@vuelidate/core";
  import AccountSidebar from "@/layout/navigation/AccountSidebar.vue";
  import { required, minLength, helpers, sameAs } from "@vuelidate/validators";
  import OnboardingPasswordInput from "@/components/onboarding/OnboardingPasswordInput";
  import { getItem, clearStorage } from "@/utils/storage";

  export default {
    name: "ChangePassword",

    components: {
      AccountSidebar,
      OnboardingPasswordInput,
    },

    beforeRouteEnter(to, from, next) {
      const loggedIn = getItem("org_details");
      const { token } = to.query;
      if (!token) {
        clearStorage();
        return next("/onboarding/signin");
      }
      if (loggedIn === null) {
        return next({ path: "/onboarding/reset-password", query: { token } });
      }
      next();
    },

    data() {
      return {
        v$: useValidate(),
        loading: false,
        payload: {
          password: "",
          confirmPassword: "",
          token: this.$route.query.token,
        },
        enableConfirmPassword: true,
        newPasswordError: {},
        confirmPasswordError: {},
        containsNumber: false,
        containsSpecial: false,
        containsUppercase: false,
        passwordLength: false,
      };
    },

    validations() {
      return {
        payload: {
          password: {
            required,
            minLength: minLength(8),
            containsUppercase: helpers.withMessage(
              "Must contain upper case letter",
              function(value) {
                return /[A-Z]/.test(value);
              }
            ),
            containsNumber: helpers.withMessage("Must contain number", function(
              value
            ) {
              return /[0-9]/.test(value);
            }),
            containsSpecial: helpers.withMessage(
              "Must contain special character [#?!@$%^&*-]",
              function(value) {
                return /[#?!@$%^&*-]/.test(value);
              }
            ),
          },
          confirmPassword: {
            required,
            sameAs: sameAs(this.payload.password),
          },
        },
      };
    },

    methods: {
      ...mapActions("organisationModule", ["resetPassword"]),
      ...mapActions(["showToast"]),

      async submitData() {
        this.v$.$validate();
        const hasValue = Object.keys(this.payload).every(
          (key) => this.payload[key]
        );
        if (this.v$.$errors.length === 0 && hasValue) {
          this.loading = true;
          delete this.payload.confirmPassword;
          const response = await this.resetPassword(this.payload);
          if (response) {
            const { message } = response.data;
            this.showToast({
              description: message,
              display: true,
              type: "success",
            });
            this.$router.push("/dashboard/account");
          }
          this.loading = false;
        }
      },

      setNewPassword(password) {
        this.payload.confirmPassword = "";
        this.payload.password = password;
        if (password.length >= 8) {
          this.enableConfirmPassword = false;
        } else {
          this.enableConfirmPassword = true;
        }
      },

      setConfirmPassword(password) {
        this.payload.confirmPassword = password;
      },

      validateMethod(field) {
        switch (field) {
          case "Enter new password":
            this.v$.$reset();
            this.v$.payload.password.$touch();
            if (this.v$.$errors.length === 0)
              return (this.newPasswordError = {});
            this.newPasswordError = this.v$.$errors[0];
            break;
          case "Confirm new password":
            this.v$.$reset();
            this.v$.payload.confirmPassword.$touch();
            if (this.v$.$errors.length === 0)
              return (this.confirmPasswordError = {});
            this.confirmPasswordError = this.v$.$errors[0];
            break;

          default:
            break;
        }
      },

      checkPassword(password) {
        this.containsNumber = /[0-9]/.test(password);
        this.containsSpecial = /[#?!@$%^&*-]/.test(password);
        this.containsUppercase = /[A-Z]/.test(password);
        this.passwordLength = password.length >= 8;
      },
    },

    watch: {
      newPasswordError(newValue, oldValue) {
        deep: true;
        immediate: true;
        if (newValue !== oldValue) {
          this.checkPassword(this.payload.password);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
.change-password {
  @include fadeIn;

  &__container {
  }

  &__header {
    padding: 24px 0;
    margin-bottom: 24px;
  }

  &__title {
    font-family: 'CircularStd-Medium', sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 4px;
  }

  &__subtitle {
    font-size: 14px;
    color: #64748B;
    margin: 0;
  }

  &__form-wrapper {
    padding: 24px;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__input-border {
    border: 1px solid #E2E8F0 !important;
    border-radius: 10px !important;
    
    &:focus-within {
      border-color: #001240 !important;
    }
  }

  &__requirements {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding: 16px;
    background: #F8FAFC;
    border-radius: 10px;
  }

  &__requirement {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #64748B;
  }

  &__check {
    flex-shrink: 0;
    color: #CBD5E1;

    &--valid {
      color: #22C55E;
    }
  }

  &__confirm-field {
    margin-top: 4px;
  }

  &__submit {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 48px;
    width: 100%;
    max-width: 200px;
    background: #001240;
    border: none;
    border-radius: 10px;
    font-family: 'CircularStd-Medium', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #FFFFFF;
    cursor: pointer;
    transition: background-color 0.2s ease;
    margin-top: 8px;

    &:hover:not(:disabled) {
      background: #06206B;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &__spinner {
    width: 24px;
    height: 24px;
  }
}

@media (max-width: 640px) {
  .change-password {
    &__requirements {
      grid-template-columns: 1fr;
    }

    &__submit {
      max-width: 100%;
    }
  }
}
</style>
